// layout/MainLayout.js
import React, { useState, useEffect } from 'react';
import { styled, createTheme, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import '../styles/modern-theme.css';
import logo from '../assets/images/logo.png';
import { filterMenuItemsByPermissions } from '../utils/permissions';
import MuiDrawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import MuiAppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Badge from '@mui/material/Badge';
import Container from '@mui/material/Container';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import NotificationsIcon from '@mui/icons-material/Notifications';
import LogoutIcon from '@mui/icons-material/Logout';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import DashboardIcon from '@mui/icons-material/Dashboard';
import InboxIcon from '@mui/icons-material/Inbox';
import AssessmentIcon from '@mui/icons-material/Assessment';
import HandshakeIcon from '@mui/icons-material/Handshake';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import SendIcon from '@mui/icons-material/Send';
import SettingsIcon from '@mui/icons-material/Settings';
import HelpIcon from '@mui/icons-material/Help';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import PublicIcon from '@mui/icons-material/Public';
import ChatIcon from '@mui/icons-material/Chat';
import PersonIcon from '@mui/icons-material/Person';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import ReportIcon from '@mui/icons-material/Report';
import ListSubheader from '@mui/material/ListSubheader';
import InsightsIcon from '@mui/icons-material/Insights';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import PaymentIcon from '@mui/icons-material/Payment';
import PeopleIcon from '@mui/icons-material/People';
import SecurityIcon from '@mui/icons-material/Security';
import InfoIcon from '@mui/icons-material/Info';
import Tooltip from '@mui/material/Tooltip';
import Link from '@mui/material/Link';
import { useNavigate, useLocation } from 'react-router-dom';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';

const drawerWidth = 240;

// AppBar, Drawer und weitere stilistische Konfigurationen hier
function Copyright(props) {
    return (
      <Typography variant="body2" color="text.secondary" align="center" {...props}>
        {'Copyright © '}
        <Link color="inherit" href="https://argus-health.de" target="_blank" rel="noopener noreferrer">
          argus-health.de
        </Link>{' '}
        {new Date().getFullYear()}
        {'.'}
      </Typography>
    );
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    '& .MuiDrawer-paper': {
      position: 'relative',
      whiteSpace: 'nowrap',
      width: drawerWidth,
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
      boxSizing: 'border-box',
      ...(!open && {
        overflowX: 'hidden',
        transition: theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen,
        }),
        width: theme.spacing(7),
        [theme.breakpoints.up('sm')]: {
          width: theme.spacing(9),
        },
      }),
    },
  }),
);

const MainLayout = ({ children }) => {
  const [open, setOpen] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  const toggleDrawer = () => {
    setOpen(!open);
  };

  // Nur Debug-Ausgabe, keine automatische Umleitung
  useEffect(() => {
    const token = localStorage.getItem('token');
    console.log('Token vorhanden:', !!token);
    // Wir leiten nicht automatisch um, damit die Seiten für Demo-Zwecke zugänglich bleiben
  }, []);
  
  // Logout-Funktion
  const handleLogout = () => {
    try {
      console.log('Logout-Funktion ausgelöst');
      
      // Token aus dem lokalen Speicher entfernen
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      
      console.log('Token entfernt, navigiere zu /login');
      
      // Zur Login-Seite umleiten
      window.location.href = '/login';
    } catch (error) {
      console.error('Fehler beim Logout:', error);
    }
  };

  // Anzahl neuer Anfragen für Badge (temporär hardcoded für Demo)
  const newRequestsCount = 2;

  // Menüpunkte für die Navigation
  const providerMenuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/', requiredPermission: 'view_dashboard' },
    {
      text: 'Anfragen',
      icon: <InboxIcon />,
      path: '/incoming-requests',
      badge: newRequestsCount,
      requiredPermission: 'view_results_delivery'
    },
    { text: 'Profile Management', icon: <AccountTreeIcon />, path: '/profile-management', requiredPermission: 'view_profile_management' },
    { text: 'Data Mapping', icon: <AutoFixHighIcon />, path: '/data-mapping', requiredPermission: 'view_data_mapping' },
    { text: 'Vision Zero Network', icon: <PublicIcon />, path: '/network', requiredPermission: 'view_vision_zero' },
    { text: 'OMOP Data Explorer', icon: <ChatIcon />, path: '/omop-chat', requiredPermission: 'view_omop_explorer' },
  ];

  const clientMenuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/client/dashboard' },
    { text: 'Meine Anfragen', icon: <PersonIcon />, path: '/client/my-requests' },
    { text: 'Neue Anfrage', icon: <RequestQuoteIcon />, path: '/client/new-request' },
    { text: 'Berichte', icon: <ReportIcon />, path: '/client/reports' },
  ];

  const futureClientItems = [
    { text: 'Datenanalyse', icon: <InsightsIcon />, path: '/client/analytics', disabled: true },
    { text: 'Partnerkliniken', icon: <LocalHospitalIcon />, path: '/client/hospitals', disabled: true },
  ];

  const futureBillingItems = [
    { text: 'Abrechnung', icon: <PaymentIcon />, path: '/billing', disabled: true },
    { text: 'Team & Berechtigungen', icon: <PeopleIcon />, path: '/team', disabled: true },
    { text: 'Datensicherheit', icon: <SecurityIcon />, path: '/data-security', disabled: true },
    { text: 'Über ARGUS', icon: <InfoIcon />, path: '/about', disabled: true },
  ];

  const commonMenuItems = [
    { text: 'Einstellungen', icon: <SettingsIcon />, path: '/settings' },
    { text: 'Hilfe & Support', icon: <HelpIcon />, path: '/help' },
  ];

  // Modern theme with professional color scheme based on corporate identity
  const modernTheme = createTheme({
    palette: {
      primary: {
        main: '#ed7d31', // Angepasst an die Corporate Design-Vorgabe
        light: '#FFA64D',
        dark: '#CC6200',
      },
      secondary: {
        main: '#00B8D4',
        light: '#62EBFF',
        dark: '#0088A3',
      },
      background: {
        default: '#F8F9FA',
        paper: '#ffffff',
      },
      text: {
        primary: '#2D3748',
        secondary: '#718096',
      },
      error: {
        main: '#F56565',
      },
      warning: {
        main: '#F6AD55',
      },
      info: {
        main: '#4299E1',
      },
      success: {
        main: '#48BB78',
      },
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      h1: {
        fontWeight: 700,
      },
      h2: {
        fontWeight: 700,
      },
      h3: {
        fontWeight: 600,
      },
      h4: {
        fontWeight: 600,
      },
      h5: {
        fontWeight: 600,
      },
      h6: {
        fontWeight: 600,
      },
      button: {
        fontWeight: 600,
        textTransform: 'none',
      },
    },
    shape: {
      borderRadius: 8,
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            boxShadow: 'none',
            '&:hover': {
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
            borderRadius: 8,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
          },
        },
      },
    },
  });

  return (
    <ThemeProvider theme={modernTheme}>
      <Box sx={{ display: 'flex' }}>
        <CssBaseline />
        <AppBar 
          position="absolute" 
          open={open} 
          className="app-header"
          sx={{
            backgroundColor: 'white',
            boxShadow: 'var(--box-shadow-sm)',
            backgroundImage: 'linear-gradient(to right, rgba(255,122,0,0.03), rgba(255,166,77,0.07))',
          }}
        >
          <Toolbar sx={{ pr: '24px' }}>
            <IconButton
              edge="start"
              aria-label="open drawer"
              onClick={toggleDrawer}
              sx={{ 
                marginRight: '36px', 
                ...(open && { display: 'none' }),
                color: 'primary.main',
                bgcolor: 'rgba(255,122,0,0.08)',
                '&:hover': {
                  bgcolor: 'rgba(255,122,0,0.15)',
                }
              }}
            >
              <MenuIcon />
            </IconButton>
            
            <Typography 
              component="h1" 
              variant="h6" 
              noWrap 
              sx={{ 
                flexGrow: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                fontWeight: 600,
                color: 'text.primary' // Dunkelgrau statt Orange
              }}
            >
              Hermes
              {process.env.REACT_APP_ENVIRONMENT && (
                <span style={{
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  padding: '2px 8px',
                  borderRadius: '4px',
                  backgroundColor: process.env.REACT_APP_ENVIRONMENT === 'DEV' ? '#4299E1' : '#F6AD55',
                  color: 'white',
                  letterSpacing: '1px',
                  marginLeft: '8px',
                }}>
                  {process.env.REACT_APP_ENVIRONMENT}
                </span>
              )}
            </Typography>
            
            <IconButton color="inherit">
              <Badge badgeContent={4} color="primary">
                <NotificationsIcon />
              </Badge>
            </IconButton>
            <IconButton 
              color="inherit" 
              onClick={handleLogout}
              sx={{ 
                ml: 1,
                color: 'text.secondary',
                '&:hover': { color: 'error.main' }
              }}
              aria-label="logout"
            >
              <LogoutIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        <Drawer 
          variant="permanent" 
          open={open}
          sx={{
            '& .MuiDrawer-paper': {
              backgroundColor: 'white',
              boxShadow: 'var(--box-shadow)',
              borderRight: 'none',
            }
          }}
        >
          <Box 
            className="sidebar-brand"
            sx={{
              background: 'linear-gradient(135deg, rgba(255,122,0,0.08) 0%, rgba(255,166,77,0.12) 100%)',
              py: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: open ? 'space-between' : 'center',
              borderBottom: '1px solid',
              borderColor: 'rgba(255,122,0,0.1)',
            }}
          >
            {open && (
              <div className="logo-container" style={{ marginLeft: 0 }}>
                <img
                  src={logo}
                  alt="ARGUS Logo"
                  style={{ height: 40 }}
                />
              </div>
            )}
            <IconButton 
              onClick={toggleDrawer} 
              sx={{ 
                color: 'primary.main',
                bgcolor: 'rgba(255,122,0,0.08)',
                '&:hover': {
                  bgcolor: 'rgba(255,122,0,0.15)',
                },
                ...(open ? { mr: 1 } : { mx: 'auto' })
              }}
            >
              <ChevronLeftIcon />
            </IconButton>
          </Box>
          <Divider />
          <List
            component="nav"
            sx={{
              '& .MuiListItemButton-root': {
                borderRadius: 1,
                mx: 1,
                mb: 0.5,
                '&:hover': {
                  backgroundColor: 'rgba(255, 122, 0, 0.08)',
                },
                '&.Mui-selected': {
                  backgroundColor: 'rgba(255, 122, 0, 0.12)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 122, 0, 0.15)',
                  },
                  '& .MuiListItemIcon-root': {
                    color: 'primary.main',
                  },
                  '& .MuiListItemText-primary': {
                    fontWeight: 600,
                    color: 'primary.main',
                  },
                },
              },
              '& .MuiListItemIcon-root': {
                minWidth: 40,
                color: 'text.secondary',
              },
              '& .MuiListSubheader-root': {
                backgroundColor: 'transparent',
                color: 'text.primary',
                fontWeight: 600,
                fontSize: '0.875rem',
                lineHeight: '1.5',
                px: 2,
                py: 1,
              },
            }}
          >
            {/* Provider Section */}
            {open && (
              <ListSubheader component="div" disableSticky>
                Provider Portal
              </ListSubheader>
            )}
            {filterMenuItemsByPermissions(providerMenuItems).map((item) => (
              <ListItemButton
                key={item.text}
                onClick={() => navigate(item.path)}
                selected={location.pathname === item.path}
              >
                <ListItemIcon>
                  {item.icon}
                </ListItemIcon>
                {open && <ListItemText primary={item.text} />}
                {item.badge && (
                  <Box
                    sx={{
                      bgcolor: 'primary.main',
                      color: 'white',
                      borderRadius: '50%',
                      width: open ? 24 : 20,
                      height: open ? 24 : 20,
                      minWidth: open ? 24 : 20,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      ml: open ? 1 : 'auto',
                      mr: open ? 0 : 'auto',
                      position: open ? 'relative' : 'absolute',
                      right: open ? 'auto' : 10,
                      whiteSpace: 'nowrap',
                      overflow: 'visible'
                    }}
                  >
                    {item.badge}
                  </Box>
                )}
              </ListItemButton>
            ))}

            {/* Client Section */}
            {open && (
              <>
                <Box sx={{ my: 1 }}>
                  <Divider />
                </Box>
                <ListSubheader component="div" disableSticky>
                  Client Portal
                </ListSubheader>
              </>
            )}
            {clientMenuItems.map((item) => (
              <ListItemButton
                key={item.text}
                onClick={() => navigate(item.path)}
                selected={location.pathname === item.path}
              >
                <ListItemIcon>
                  {item.icon}
                </ListItemIcon>
                {open && <ListItemText primary={item.text} />}
              </ListItemButton>
            ))}

            {/* Future Client Features */}
            {futureClientItems.map((item) => (
              <Tooltip key={item.text} title="Demnächst verfügbar" placement="right">
                <span>
                  <ListItemButton
                    disabled={item.disabled}
                    sx={{
                      opacity: 0.8,
                      cursor: 'not-allowed',
                      borderRadius: 1,
                      mx: 1,
                      mb: 0.5,
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      },
                    }}
                  >
                    <ListItemIcon sx={{
                      color: 'text.secondary',
                      minWidth: 40
                    }}>
                      {item.icon}
                    </ListItemIcon>
                    {open && (
                      <ListItemText
                        primary={item.text}
                        primaryTypographyProps={{
                          color: 'text.secondary',
                          fontSize: '0.95rem'
                        }}
                      />
                    )}
                  </ListItemButton>
                </span>
              </Tooltip>
            ))}

            {/* Common Section */}
            {open && (
              <>
                <Box sx={{ my: 1 }}>
                  <Divider />
                </Box>
                <ListSubheader component="div" disableSticky>
                  Allgemein
                </ListSubheader>
              </>
            )}
            {commonMenuItems.map((item) => (
              <ListItemButton
                key={item.text}
                onClick={() => navigate(item.path)}
                selected={location.pathname === item.path}
              >
                <ListItemIcon>
                  {item.icon}
                </ListItemIcon>
                {open && <ListItemText primary={item.text} />}
              </ListItemButton>
            ))}

            {/* Future Development Section */}
            {open && (
              <>
                <Box sx={{ my: 1 }}>
                  <Divider />
                </Box>
                <ListSubheader component="div" disableSticky>
                  In Entwicklung
                </ListSubheader>
              </>
            )}
            {futureBillingItems.map((item) => (
              <Tooltip key={item.text} title="Demnächst verfügbar" placement="right">
                <span>
                  <ListItemButton
                    disabled={item.disabled}
                    sx={{
                      opacity: 0.8,
                      cursor: 'not-allowed',
                      borderRadius: 1,
                      mx: 1,
                      mb: 0.5,
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      },
                    }}
                  >
                    <ListItemIcon sx={{
                      color: 'text.secondary',
                      minWidth: 40
                    }}>
                      {item.icon}
                    </ListItemIcon>
                    {open && (
                      <ListItemText
                        primary={item.text}
                        primaryTypographyProps={{
                          color: 'text.secondary',
                          fontSize: '0.95rem'
                        }}
                      />
                    )}
                  </ListItemButton>
                </span>
              </Tooltip>
            ))}

          </List>
          
          {open && (
            <Box sx={{ mt: 'auto', mb: 2, mx: 2, p: 2, borderRadius: 1, bgcolor: 'rgba(255, 122, 0, 0.05)' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Benötigen Sie Hilfe?
              </Typography>
              <Typography variant="body2" color="primary" sx={{ fontWeight: 600 }}>
                <Link href="#" underline="none" color="inherit">
                  Support kontaktieren
                </Link>
              </Typography>
            </Box>
          )}
        </Drawer>
        <Box
          component="main"
          sx={{
            backgroundColor: (theme) => theme.palette.background.default,
            flexGrow: 1,
            height: '100vh',
            overflow: 'auto',
          }}
          className="content-area"
        >
          <Toolbar />
          <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            {children}
            <Copyright sx={{ pt: 4 }} />
          </Container>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default MainLayout;
